import { axiosAuthorized } from "../utils/axios";
import config from "../config/config";
import { ApiResponse } from "./authService"; // Re-using from authService

// ---- INTERFACES ---- //

export interface CreateBookingPayload {
  booking_vertical: "intercity" | "airport" | "hourly";
  booking_trip_type: "one-way" | "round-trip" | "pickup";
  customer_type: "B2C" | "B2B" | "guest";
  cab_type: "Hatchback" | "Sedan" | "SUV";
  cab_model: string; // e.g., "Swift", "City", or "any"
  cab_fuel_type: "Petrol" | "Diesel" | "any";
  pickup_date: string; // YYYY-MM-DD
  pickup_time: string; // HH:MM:SS
  pickup_address: string;
  return_date?: string; // YYYY-MM-DD
  return_time?: string; // HH:MM:SS
  drop_address?: string;
  source: string; // City UUID
  destination: string; // City UUID
  basefare_bt: number;
  basekms_bt: number;
  driverallowances_bt: number;
  drivernightallowances_bt: number;
  estimated_fare_bt: number;
  couponApplied?: boolean;
  coupon_code?: string;
  extrakm_rate: number;
  extrahr_rate: number;
  tollcharges_bt: number;
  permitcharges: number;
  parkingcharges: number;
  discount_amount: number;
  total_fare_before_tax: number;
  sgst_bt: number;
  cgst_bt: number;
  igst_bt: number;
  payment_mode: "cash" | "online";
  amount_paid_online?: number;
  special_request?: string;
  booking_status?: "pending" | "confirmed" | "cancelled" | "completed";
}

export interface Booking {
  booking_id: string; // UUID
  vendor_id: string; // UUID
  booking_vertical: "intercity" | "airport" | "hourly";
  booking_trip_type: "one-way" | "round-trip" | "pickup";
  customer_id: string; // UUID
  cab_type: "Hatchback" | "Sedan" | "SUV";
  cab_model: string;
  cab_fuel_type: "Petrol" | "Diesel" | "any";
  pickup_date: string; // Date
  pickup_time: string; // Time
  pickup_address: string;
  return_date?: string; // Date
  return_time?: string; // Time
  drop_address?: string;
  source: string; // UUID
  destination: string; // UUID
  SourceBooking?: {
    id: string;
    name: string;
    state: string;
  };
  DestinationBooking?: {
    id: string;
    name: string;
    state: string;
  };
  vehicle_id?: string; // UUID
  driver_id?: string; // UUID
  basefare_bt: number;
  basekms_bt: number;
  driverallowances_bt: number;
  drivernightallowances_bt: number;
  estimated_fare_bt: number;
  couponApplied: boolean;
  coupon_code?: string;
  extrakm_rate: number;
  extrahr_rate: number;
  tollcharges_bt: number;
  permitcharges: number;
  parkingcharges: number;
  discount_amount: number;
  total_fare_before_tax: number;
  sgst_bt: number;
  cgst_bt: number;
  igst_bt: number;
  payment_mode: "cash" | "online";
  amount_paid_online: number;
  additional_fare_bt?: number;
  special_request?: string;
  cancellation_reason?: string;
  booking_status:
    | "pending"
    | "assigned"
    | "ongoing"
    | "completed"
    | "cancelled";
  trip_status: "not_started" | "started" | "completed" | "cancelled";
  createdAt: string; // Date
  updatedAt: string; // Date
}

// ---- BOOKING SERVICE CLASS ---- //

class BookingService {
  // Use a relative path, as the base is set in axios config
  private readonly bookingEndpoint = config.ENDPOINTS.BOOKING;

  /**
   * Create a new booking for a user.
   * @param userId - The ID of the user creating the booking.
   * @param payload - The booking details.
   */
  async createBooking(
    userId: string,
    payload: CreateBookingPayload
  ): Promise<ApiResponse> {
    try {
      const response = await axiosAuthorized.post<ApiResponse>(
        `${this.bookingEndpoint}/create-booking/${userId}`,
        payload
      );
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  /**
   * Retrieves the details of a single booking by its ID.
   * @param bookingId - The UUID of the booking.
   */
  async getBookingById(bookingId: string): Promise<ApiResponse<Booking>> {
    try {
      const response = await axiosAuthorized.get<ApiResponse<Booking>>(
        `${this.bookingEndpoint}/get-booking/${bookingId}`
      );
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  /**
   * Retrieves all bookings for a specific customer.
   * @param customerId - The UUID of the customer.
   */
  async getAllBookingsForCustomer(
    customerId: string
  ): Promise<ApiResponse<Booking[]>> {
    try {
      const response = await axiosAuthorized.get<ApiResponse<Booking[]>>(
        `${this.bookingEndpoint}/get-bookings/${customerId}`
      );
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  /**
   * Handles API errors in a standardized way.
   */
  private handleError(error: any): Error {
    if (error.response) {
      // The request was made and the server responded with a status code
      const message =
        error.response.data?.message ||
        error.response.data?.error ||
        "An error occurred while processing your request.";
      return new Error(message);
    } else if (error.request) {
      // The request was made but no response was received
      return new Error(
        "Could not connect to the server. Please check your network connection."
      );
    } else {
      // Something happened in setting up the request that triggered an Error
      return new Error(error.message || "An unexpected error occurred.");
    }
  }
}

export const bookingService = new BookingService();
export default bookingService;
